import {createApi} from '@reduxjs/toolkit/dist/query/react';
import {axiosBaseQuery, REFETCH_POLICY} from 'src/axiosBaseQuery';
import {DefaultResponse} from 'src/types';
import {
    ChargerRemoveRequest,
    CompanyCreationRequest,
    CompanyCredentialActionRequest,
    CompanyCredentialCreateRequest,
    CompanyCredentialDTO,
    CompanyCredentialsListRequest,
    CompanyEditionRequest,
    CompanySearchRequest,
    EditCompanyResponse,
    GetCompanyByIdRequest,
    LocationAddRequest,
    LocationRemoveRequest,
    PageCompaniesDTO,
    RfidAddRequest,
    RfidRemoveRequest,
} from 'src/types/company';
import {getBaseURL} from 'src/utils/helper';

export const companyApi = createApi({
    reducerPath: 'companyApi',
    ...REFETCH_POLICY,
    baseQuery: axiosBaseQuery({
        baseUrl: String(getBaseURL() + '/companies'),
    }),
    tagTypes: ['companies', 'company', 'User', 'companyCredentials'],
    endpoints: (build) => {
        return {
            getCompanyById: build.query<EditCompanyResponse, GetCompanyByIdRequest>({
                query: ({param: {companyId, businessId}}) => ({
                    url: `/${companyId}`,
                    method: 'get',
                    params: {businessId},
                }),
                providesTags: ['company'],
            }),
            addNewCompany: build.mutation<DefaultResponse, CompanyCreationRequest>({
                query: (data) => ({url: '', method: 'post', data}),
                invalidatesTags: ['companies'],
            }),
            updateCompany: build.mutation<EditCompanyResponse, CompanyEditionRequest>({
                query: ({id, body}) => ({url: `/${id}`, method: 'put', data: body}),
                invalidatesTags: (_result, _error, {id}) => [{type: 'company', id}, 'companies'],
            }),
            setCompanyConsumptionBilling: build.mutation<
                EditCompanyResponse,
                {companyId: number; businessId: number; chargeConsumption: boolean}
            >({
                query: ({companyId, businessId, chargeConsumption}) => ({
                    url: `/${companyId}/consumption-billing`,
                    method: 'put',
                    params: {businessId, chargeConsumption},
                }),
                invalidatesTags: (_result, _error, {companyId}) => [
                    {type: 'company', id: companyId},
                    'companies',
                ],
            }),
            setCompanyChargingEmailNotifications: build.mutation<
                EditCompanyResponse,
                {companyId: number; businessId: number; chargingEmailNotifications: boolean}
            >({
                query: ({companyId, businessId, chargingEmailNotifications}) => ({
                    url: `/${companyId}/charging-email-notifications`,
                    method: 'put',
                    params: {businessId, chargingEmailNotifications},
                }),
                invalidatesTags: (_result, _error, {companyId}) => [
                    {type: 'company', id: companyId},
                    'companies',
                ],
            }),
            getCompanyCredentials: build.query<CompanyCredentialDTO[], CompanyCredentialsListRequest>({
                query: ({companyId, businessId}) => ({
                    url: `/${companyId}/credentials`,
                    method: 'get',
                    params: {businessId},
                }),
                providesTags: ['companyCredentials'],
            }),
            addCompanyCredential: build.mutation<CompanyCredentialDTO, CompanyCredentialCreateRequest>({
                query: ({companyId, businessId, data}) => ({
                    url: `/${companyId}/credentials`,
                    method: 'post',
                    params: {businessId},
                    data,
                }),
                invalidatesTags: ['companyCredentials'],
            }),
            // Sends the invitation again and replaces the outstanding link, which is what makes a lapsed
            // one recoverable without deleting the row and losing when it was first invited.
            resendCompanyCredentialInvite: build.mutation<CompanyCredentialDTO, CompanyCredentialActionRequest>({
                query: ({companyId, credentialId, businessId}) => ({
                    url: `/${companyId}/credentials/${credentialId}/invite`,
                    method: 'post',
                    params: {businessId},
                }),
                invalidatesTags: ['companyCredentials'],
            }),
            deleteCompanyCredential: build.mutation<DefaultResponse, CompanyCredentialActionRequest>({
                query: ({companyId, credentialId, businessId}) => ({
                    url: `/${companyId}/credentials/${credentialId}`,
                    method: 'delete',
                    params: {businessId},
                }),
                invalidatesTags: ['companyCredentials'],
            }),
            addRfidToCompany: build.mutation<DefaultResponse, RfidAddRequest>({
                query: ({companyId, data}) => ({
                    url: `/${companyId}/rfid/link`,
                    method: 'post',
                    params: data,
                }),
                invalidatesTags: (_result, _error, {companyId}) => [
                    {type: 'company', id: companyId},
                    'companies',
                ],
            }),
            removeRfidFromCompany: build.mutation<DefaultResponse, RfidRemoveRequest>({
                query: ({companyId, data}) => ({
                    url: `/${companyId}/rfid/unlink`,
                    method: 'post',
                    params: data,
                }),
                invalidatesTags: (_result, _error, {companyId}) => [
                    {type: 'company', id: companyId},
                    'companies',
                ],
            }),
            removeChargerFromCompany: build.mutation<DefaultResponse, ChargerRemoveRequest>({
                query: ({companyId, data}) => ({
                    url: `/${companyId}/chargers/unlink`,
                    method: 'post',
                    data,
                }),
                invalidatesTags: (_result, _error, {companyId}) => [
                    {type: 'company', id: companyId},
                    'companies',
                ],
            }),
            addCompanyToLocation: build.mutation<DefaultResponse, LocationAddRequest>({
                query: ({companyId, data}) => ({
                    url: `/${companyId}/locations/link`,
                    method: 'post',
                    data,
                }),
                invalidatesTags: (_result, _error, {companyId}) => [
                    {type: 'company', id: companyId},
                    'companies',
                ],
            }),
            removeLocationFromCompany: build.mutation<DefaultResponse, LocationRemoveRequest>({
                query: ({companyId, data}) => ({
                    url: `/${companyId}/locations/unlink`,
                    method: 'post',
                    data,
                }),
                invalidatesTags: (_result, _error, {companyId}) => [
                    {type: 'company', id: companyId},
                    'companies',
                ],
            }),
            searchCompanies: build.query<PageCompaniesDTO, CompanySearchRequest>({
                query: ({params, data}) => ({
                    url: '/search',
                    method: 'post',
                    params,
                    data,
                }),
                providesTags: ['companies'],
            }),
            deleteCompany: build.mutation<DefaultResponse, { companyId: number; businessId: number }>({
                query: ({companyId, businessId}) => ({
                    url: `/${companyId}`,
                    method: 'delete',
                    params: {businessId},
                }),
                invalidatesTags: ['companies', {type: 'User', id: 'LIST'}],
            }),
        };
    },
});
